import { useEffect, useRef, useState } from "react";

/**
 * Reveals an element when it scrolls into view. Adds the `is-visible` class
 * once and stops observing. Honors prefers-reduced-motion (revealed immediately).
 */
export function useReveal<T extends HTMLElement = HTMLDivElement>(): {
  ref: React.RefObject<T | null>;
} {
  const ref = useRef<T | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduceMotion) {
      el.classList.add("is-visible");
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            observer.unobserve(entry.target);
          }
        }
      },
      { threshold: 0.18, rootMargin: "0px 0px -8% 0px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return { ref };
}

/**
 * Animates a number from 0 → target when the element scrolls into view.
 * Reduced-motion users get the final number immediately.
 */
export function useCountUp(
  target: number,
  durationMs = 1600,
): { ref: React.RefObject<HTMLSpanElement | null>; value: number } {
  const ref = useRef<HTMLSpanElement | null>(null);
  const [value, setValue] = useState(0);
  const started = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || started.current) return;

    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduceMotion) {
      setValue(target);
      started.current = true;
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting || started.current) continue;
          started.current = true;

          const start = performance.now();
          const tick = (now: number) => {
            const t = Math.min(1, (now - start) / durationMs);
            // ease-out-expo
            const eased = t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
            setValue(Math.round(target * eased));
            if (t < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.5 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [target, durationMs]);

  return { ref, value };
}

/**
 * Apply a vertical parallax transform to the element as it crosses the viewport.
 *
 * `range` is the maximum pixel offset (in absolute terms) — the element will
 * shift between `+range/2` and `-range/2` as its centre moves from the bottom
 * to the top of the viewport. A negative `range` produces "slower-than-page"
 * drift (element appears to stay still longer); a positive `range` produces
 * "faster-than-page" drift (element appears to emerge or rise).
 *
 * Honors prefers-reduced-motion (no transform applied).
 * Uses requestAnimationFrame and a passive scroll listener.
 */
export function useScrollParallax<T extends HTMLElement = HTMLDivElement>(
  range = -160,
): React.RefObject<T | null> {
  const ref = useRef<T | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduceMotion) return;

    let raf = 0;
    const update = () => {
      raf = 0;
      const rect = el.getBoundingClientRect();
      const viewH = window.innerHeight || document.documentElement.clientHeight;
      // progress 0 = element centre at viewport top, 1 = centre at viewport bottom
      const progress = (rect.top + rect.height / 2) / viewH;
      // Map to [-0.5, 0.5] then scale by range
      const offset = (progress - 0.5) * range;
      el.style.transform = `translate3d(0, ${offset.toFixed(2)}px, 0)`;
    };

    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(update);
    };

    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [range]);

  return ref;
}

/** Locks body scroll while the boolean is true. Used by mobile menu sheet. */
export function useScrollLock(locked: boolean): void {
  useEffect(() => {
    if (!locked) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = original;
    };
  }, [locked]);
}
